import { Link } from "react-router-dom";
import {
  ArrowRight, Monitor, Database, Wifi, Cloud, Bell, Wrench, FileCheck,
  Search, Activity, Cpu, Shield, BarChart3
} from "lucide-react";

const coreServices = [
  {
    icon: Monitor,
    title: "Managed OS Services",
    items: ["Linux, Windows, Unix platform support", "Security hardening, patching, lifecycle management", "Performance optimization & compliance readiness"],
  },
  {
    icon: Database,
    title: "Managed Database Services",
    items: ["Performance tuning, replication setup", "Backup, scaling & disaster recovery planning", "SQL, Oracle, PostgreSQL, NoSQL expertise"],
  },
  {
    icon: Wifi,
    title: "Remote Infrastructure Management",
    items: ["Centralized server, storage & network management", "Real-time monitoring & rapid issue resolution", "Proactive optimization via performance insights"],
  },
  {
    icon: Cloud,
    title: "Cloud Infrastructure Management",
    items: ["Multi-cloud support (AWS, Azure, GCP)", "Workload balancing, patching & access control", "Governance & compliance across hybrid environments"],
  },
  {
    icon: Bell,
    title: "Infrastructure Monitoring & NOC",
    items: ["24x7 system health monitoring & predictive alerts", "Root cause analysis with SLA-based escalation", "Intrusion detection & traffic optimization"],
  },
  {
    icon: Wrench,
    title: "ITSM Integration",
    items: ["ServiceNow, BMC Remedy, Freshservice integration", "Unified ticketing, reporting & change management", "End-to-end visibility across IT operations"],
  },
  {
    icon: FileCheck,
    title: "Compliance & Audit Support",
    items: ["Audit readiness for ISO 27001, SOC2, DPDPA", "Policy alignment & continuous monitoring", "Transparent compliance reporting"],
  },
];

const deliveryPhases = [
  { icon: Search, title: "Assess & Align", desc: "Evaluate infrastructure, risks, and KPIs" },
  { icon: Activity, title: "Monitor & Maintain", desc: "Continuous monitoring, patching, dashboards" },
  { icon: Cpu, title: "Optimize & Automate", desc: "Predictive analytics, automation workflows" },
  { icon: Shield, title: "Protect & Scale", desc: "Secure endpoints, scalable workloads" },
  { icon: BarChart3, title: "Report & Improve", desc: "SLA compliance, continuous improvement" },
];

const businessBenefits = [
  "Reduced downtime through proactive remediation",
  "Increased efficiency with optimized resource usage",
  "Scalable models for on-prem, cloud, and hybrid",
  "Consistent regulatory compliance across environments",
  "24x7 global expert support with strict SLAs",
];

export default function InfraServicePage() {
  return (
    <div data-testid="infra-service-page">
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">
              IT Infrastructure Management
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0A0A0A] mb-8">
              Agility, Performance, and Always On Support
            </h1>
            <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed mb-8">
              Modern enterprises need resilient, secure, and flexible infrastructure for
              hybrid workloads, distributed teams, and mission-critical operations. We deliver
              24x7 monitoring, proactive issue prevention, and seamless scalability.
            </p>
            <Link
              to="/contact"
              data-testid="infra-hero-cta"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001F70] transition-colors"
            >
              Request Assessment <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Image Banner */}
      <section className="w-full h-[300px] lg:h-[400px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/5203849/pexels-photo-5203849.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Server room infrastructure"
          className="w-full h-full object-cover"
        />
      </section>

      {/* Core Services */}
      <section data-testid="infra-services-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              Core Services
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Comprehensive infrastructure management
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-gray-200 bg-white">
            {coreServices.map((service, i) => (
              <div
                key={service.title}
                data-testid={`infra-service-item-${i}`}
                className={`p-8 lg:p-10 ${
                  (i + 1) % 3 !== 0 ? "lg:border-r border-gray-200" : ""
                } ${(i + 1) % 2 !== 0 && i < 6 ? "sm:border-r lg:border-r-0 border-gray-200" : ""} ${
                  i < coreServices.length - (coreServices.length % 3 || 3) ? "border-b border-gray-200" : ""
                } border-b lg:border-b border-gray-200 last:border-b-0`}
              >
                <service.icon className="w-6 h-6 text-[#002FA7] mb-5" />
                <h3 className="font-heading text-lg font-semibold text-[#0A0A0A] mb-4">
                  {service.title}
                </h3>
                <ul className="space-y-2">
                  {service.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#4B5563]">
                      <span className="w-1 h-1 rounded-full bg-[#002FA7] mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Approach */}
      <section data-testid="delivery-approach-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
              Delivery Approach
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A]">
              Phased infrastructure delivery
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border border-gray-200">
            {deliveryPhases.map((phase, i) => (
              <div
                key={phase.title}
                data-testid={`delivery-phase-${i}`}
                className={`p-6 lg:p-8 ${i < 4 ? "lg:border-r border-gray-200" : ""} ${
                  i < 4 ? "sm:border-b lg:border-b-0 border-gray-200" : ""
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-heading text-2xl font-bold text-[#002FA7] opacity-30">
                    0{i + 1}
                  </span>
                  <phase.icon className="w-5 h-5 text-[#002FA7]" />
                </div>
                <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">{phase.title}</h4>
                <p className="text-xs text-[#4B5563] leading-relaxed">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Benefits */}
      <section data-testid="infra-benefits-section" className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">
                Business Benefits
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A] mb-6">
                Why TEQXL for infrastructure
              </h2>
              <p className="text-base text-[#4B5563] leading-relaxed">
                Our infrastructure management services ensure your systems are always
                available, optimized, and compliant — so you can focus on your core business.
              </p>
            </div>
            <div className="border border-gray-200 bg-white">
              {businessBenefits.map((benefit, i) => (
                <div
                  key={benefit}
                  className={`flex items-start gap-4 p-6 ${
                    i < businessBenefits.length - 1 ? "border-b border-gray-200" : ""
                  }`}
                >
                  <span className="font-heading text-sm font-bold text-[#002FA7]">0{i + 1}</span>
                  <p className="text-sm text-[#0A0A0A]">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-white mb-6">
            Build resilient infrastructure that powers growth
          </h2>
          <p className="text-base text-blue-200 max-w-xl mx-auto mb-10 leading-relaxed">
            Let us manage your infrastructure while you focus on what matters most —
            your business.
          </p>
          <Link
            to="/contact"
            data-testid="infra-cta-link"
            className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#002FA7] text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Start a Conversation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
